import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

import { getToolDisplayInfo } from "../toolConfig";
import { ChatMessage, ToolCallData } from "../stores";
import { MessageRole, ToolCallStatus } from "../types";
import { EntityPreview, parseEntityPreview } from "../utils/entityPreview";
import { isComputerAgentToolName, isCreateAnalyticsGraphToolName } from "../utils/toolNameMatchers";
import { ToolCallWidget } from "../widgets";
import { EntityPreviewCard, ToolIcon } from "../widgets/ToolCallWidget";

import {
  TOOL_STACK_BUTTON_WIDTH_TRANSITION_MS,
  TOOL_STACK_LIST_HEIGHT_TRANSITION_MS,
  TOOL_STACK_MAX_VISIBLE_ICONS,
  TOOL_STACK_NESTED_ITEM_DELAY_MS,
  TOOL_STACK_NESTED_ITEM_TRANSITION_MS,
} from "./toolStackConstants";

import { cn } from "@/lib/utils";

/** Completed, failed, or errored tools stack together like “multiple steps”. */
const STACKABLE_TERMINAL_STATUSES: readonly ToolCallStatus[] = [
  ToolCallStatus.COMPLETED,
  ToolCallStatus.ERROR,
  ToolCallStatus.FAILED,
];

interface ParsedStackItem {
  message: ChatMessage;
  toolCall: ToolCallData;
  preview: EntityPreview | null;
}

export const isStackableTerminalToolCallMessage = (message: ChatMessage) =>
  message.role === MessageRole.TOOL_CALL &&
  message.toolCall &&
  !isComputerAgentToolName(message.toolCall.name) &&
  !isCreateAnalyticsGraphToolName(message.toolCall.name) &&
  STACKABLE_TERMINAL_STATUSES.includes(message.toolCall.status);

const canRenderStackItem = (item: ParsedStackItem) => {
  const { status } = item.toolCall;
  if (status === ToolCallStatus.ERROR || status === ToolCallStatus.FAILED) {
    return true;
  }

  if (item.preview) {
    return true;
  }

  const displayInfo = getToolDisplayInfo(item.toolCall);
  return !displayInfo.shouldHideWhenComplete || !!item.toolCall.navigation?.length;
};

const EntityPreviewList = ({
  items,
  className,
}: {
  items: ParsedStackItem[];
  className?: string;
}) => (
  <div className={cn("flex w-fit max-w-full flex-col items-start gap-0.5", className)}>
    {items.map((item, index) =>
      item.preview ? (
        <EntityPreviewCard
          key={item.toolCall.id ?? `${item.toolCall.name}-preview-${index}`}
          preview={item.preview}
        />
      ) : null,
    )}
  </div>
);

export const CompletedToolCallStack = ({ toolCalls }: { toolCalls: ChatMessage[] }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const parsedItems = toolCalls.flatMap(message => {
    if (!message.toolCall) {
      return [];
    }
    return [
      {
        message,
        toolCall: message.toolCall,
        preview:
          message.toolCall.status === ToolCallStatus.COMPLETED
            ? parseEntityPreview(message.toolCall)
            : null,
      },
    ];
  });

  const renderableItems = parsedItems.filter(canRenderStackItem);
  const previewItems = renderableItems.filter(
    item => item.preview && item.toolCall.status === ToolCallStatus.COMPLETED,
  );
  const supportingItems = renderableItems.filter(item => {
    if (
      item.toolCall.status === ToolCallStatus.ERROR ||
      item.toolCall.status === ToolCallStatus.FAILED
    ) {
      return true;
    }
    return !item.preview;
  });

  if (renderableItems.length === 0) {
    return null;
  }

  if (renderableItems.length === 1 && previewItems.length === 1 && previewItems[0].preview) {
    return <EntityPreviewCard preview={previewItems[0].preview} />;
  }

  if (renderableItems.length === 1) {
    return (
      <ToolCallWidget toolCall={renderableItems[0].toolCall} hideNavigation useNavigationRow />
    );
  }

  const stackHasFailure = supportingItems.some(
    item =>
      item.toolCall.status === ToolCallStatus.ERROR ||
      item.toolCall.status === ToolCallStatus.FAILED,
  );

  const stackSource = supportingItems.length > 0 ? supportingItems : renderableItems;
  const visibleItems = stackSource.slice(0, TOOL_STACK_MAX_VISIBLE_ICONS);
  const hiddenCount = Math.max(stackSource.length - visibleItems.length, 0);

  return (
    <div className="-my-0.5 inline-flex w-fit max-w-full flex-col items-start gap-1">
      <motion.button
        layout
        transition={{
          layout: {
            duration: TOOL_STACK_BUTTON_WIDTH_TRANSITION_MS / 1000,
            ease: "easeOut",
          },
        }}
        type="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(current => !current)}
        className="group/tool-stack inline-flex w-fit max-w-full items-center gap-2 rounded-md px-1 py-0.5 text-left transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
      >
        <div className="flex h-5 min-w-0 items-center pl-0.5">
          {visibleItems.map((item, index) => {
            const displayInfo = getToolDisplayInfo(item.toolCall);
            return (
              <span
                key={item.toolCall.id ?? `${item.toolCall.name}-${index}`}
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full border border-white bg-gray-50 shadow-sm ring-1 ring-gray-100 transition-colors group-hover/tool-stack:bg-white",
                  index > 0 && "-ml-1.5",
                )}
                style={{ zIndex: visibleItems.length - index }}
              >
                <ToolIcon
                  type={displayInfo.type}
                  className="h-3 w-3 text-gray-400 transition-colors group-hover/tool-stack:text-gray-500"
                />
              </span>
            );
          })}
          {hiddenCount > 0 && (
            <span className="-ml-1.5 flex h-5 min-w-5 items-center justify-center rounded-full border border-white bg-gray-100 px-1 text-[10px] font-medium leading-none text-gray-500 shadow-sm ring-1 ring-gray-100 transition-colors group-hover/tool-stack:bg-gray-50 group-hover/tool-stack:text-gray-600">
              +{hiddenCount}
            </span>
          )}
        </div>
        <span className="truncate text-xs font-medium leading-5 text-gray-500 transition-colors group-hover/tool-stack:text-gray-700">
          {stackHasFailure ? "Multiple steps" : "Completed multiple steps"}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-gray-400 transition-all duration-150 group-hover/tool-stack:text-gray-600",
            isExpanded && "rotate-180",
          )}
        />
      </motion.button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="tool-stack-list"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{
              duration: TOOL_STACK_LIST_HEIGHT_TRANSITION_MS / 1000,
              ease: "easeInOut",
            }}
            className="w-fit max-w-full overflow-hidden"
          >
            <div className="ml-5 flex w-fit max-w-[calc(100%-1.25rem)] flex-col items-start gap-0.5 pl-1">
              {previewItems.length > 0 ? <EntityPreviewList items={previewItems} /> : null}
              {supportingItems.map((item, index) => (
                <motion.div
                  key={item.toolCall.id ?? `${item.toolCall.name}-${index}`}
                  initial={{ opacity: 0, x: -3 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: TOOL_STACK_NESTED_ITEM_TRANSITION_MS / 1000,
                    delay: ((index + 1) * TOOL_STACK_NESTED_ITEM_DELAY_MS) / 1000,
                    ease: "easeOut",
                  }}
                  className="w-fit max-w-full"
                >
                  <ToolCallWidget toolCall={item.toolCall} hideNavigation useNavigationRow />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
